/*
================================================================================
                    WPQ GET DETAILS - BUSINESS LOGIC
================================================================================

PURPOSE: Process-specific qualification arrays (up to 3 processes)

ARRAY STRUCTURE:
[{Actual_Value: ["value"]}, {Range_Qualified: ["range"]}]

WI FIELD EXTRACTION (for backend):
• WPQ_FM_Arc_transfermode[1].Range_Qualified[0] → WI_WPQ_FM_Arc_transfermode
• WPQ_Groove_610[1].Range_Qualified → WI_WPQ_610_Positions
• Position extraction: Only "true" + "Checked" positions

WI INTEGRATION FIELDS (Used in API):
• WPQ_WelderId → Welder identification for slip generation
• WPQ_WelderName → Welder name display in WI slip
• WPQ_Process_Category → GSI indexing for welder queries
• wld_createdon → Last weld date tracking and 178-day validity
• WPQ_Welder_ExpiresOn → 178-day qualification validity check
• WPQ_BM_WTBM_Pno → P-Number matching with WPS base metals
• WPQ_BMT_Pipe_Diameter_RangeQ_Overlay → Pipe diameter qualification range
• WPQ_BMT_Plate_thickness_RangeQ_Overlay → Plate thickness qualification range
• WI_WPQ_610_Positions → Welding positions qualified for >610mm diameter
• WI_WPQ_73to610_Positions → Welding positions qualified for 73-610mm diameter
• WI_WPQ_73_Positions → Welding positions qualified for <73mm diameter
• WI_WPQ_FM_Arc_transfermode → Arc transfer mode matching with WPS
• WI_WPQ_FM_Product_Form → Filler metal product form validation
• WI_WPQ_FM_Current_Polarity → Current polarity compatibility check
• WI_WPQ_FM_F_No → F-Number matching for filler metal compatibility
• WI_WPQ_FM_Consumable_Insert → Consumable insert requirement (Groove only)
• WI_WPQ_SlipGenerationStatus → Tracks if WPQ used in WI slip generation
• WPSH_Status → Must be 'Approved' for WI slip eligibility
• wld_process_status → Must be 'Active' for current qualification
• wld_WPSType → Joint type matching (Groove/Overlay/Tubesheet)

VALIDATION:
• Type "S": Simple arrays
• Type "SA": Complex nested arrays
• Both Actual_Value and Range_Qualified required

================================================================================
*/

import { Row, Tab, Tabs } from "react-bootstrap";
import {
  useState,
  useImperativeHandle,
  forwardRef,
  useEffect,
  useRef,
} from "react";
import { WPQHeader } from "./WPQObjects/WPQHeader";
import { WPQ_Groove_Detail, errorprocess } from "./WPQObjects/WPQ_entity";
import { Status, Action, MessageTypes, ToastMessages } from "../utility/common/constants/CommonConstants"
import { ToolError } from "../utility/forms/inputs/InputsTransaction"
import { showSuccess, showWarning } from "../utility/ui/notifications/Toaster";
import WPQGetDetailProcess from "./WPQGetDetailProcess";
import { AddNewGrooveWPQ } from "../../Services/wpq_groove-services";
import { DotLoader } from "react-spinners";
import { handleCount, createWPQRangeQualPostlist, handleWPQGeneralSubmit, handleWPQGeneralSubmitTemp, emptyErrorWPQDetail } from "./WPQObjects/WPQCommonUtils";
import { AddNewDetail } from "../../Services/wpq_details-service";
import PropTypes from "prop-types";

const WPQ_GetDetails = forwardRef(
  (
    {
      headerObj,
      setheaderObj,
      detailObj,
      setdetailObj,
      setKey,
      tabno,
      isDisabled,
      isReadonly,
      action,
      setAction
    },
    ref
  ) => {
    WPQ_GetDetails.propTypes = {
      headerObj: PropTypes.array,
      setheaderObj: PropTypes.func,
      setdetailObj: PropTypes.func,
      setKey: PropTypes.func,
      detailObj: PropTypes.array,
      isDisabled: PropTypes.bool,
      isReadonly: PropTypes.bool,
      tabno: PropTypes.number,
      action: PropTypes.string,
      setAction: PropTypes.func
    };

    const [keyprocess, setKeyprocess] = useState(1);
    const [processErrors, setProcessErrors] = useState([false, false, false]);
    const firstChildRef = useRef();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState([errorprocess(), errorprocess(), errorprocess()]);
    const [IsEdited, setIsEdited] = useState(false);

    useImperativeHandle(ref, () => ({
      funcOnNext2: async () => {
        return handleSubmit();
      },

      funcOnNextTemp2: async () => {
        return handleSubmitTemp();
      },
    }));

    const setErrorAt = (i) => (updater) => {
      setError(prev => {
        const next = [...prev];
        next[i] = typeof updater === 'function' ? updater(next[i]) : updater;
        return next;
      });
    };

    const validateTemp = async () => {
      let isError = false;
      let temperror = emptyErrorWPQDetail(error[0], setErrorAt(0), isError);
      return { isError, temperror };
    };

    const handleSubmitTemp = () => handleWPQGeneralSubmitTemp(
      IsEdited,
      validateTemp,
      () => handleAdd(headerObj?.WPSH_Status),
      setLoading,
      setIsEdited,
      headerObj?.WPSH_Status || Status.StatusTempSaved
    );

    const handleSubmit = async () => {
      const result = await handleWPQGeneralSubmit(
        validate,
        () => handleAdd(""),
        setLoading,
        setIsEdited,
        Status.StatusPending
      );

      if (result === MessageTypes.Success) {
        setKey(+tabno + 1);
      }

      return result;
    };

    const validate = async () => {
      let detaildata = detailObj?.filter((item) => item.WPQ_Process !== "");

      let isError = false;
      setProcessErrors([false, false, false]);
      setError([errorprocess(), errorprocess(), errorprocess()]);
      let errortype = WPQ_Groove_Detail;
      let entries = Object.entries(detaildata);

      // VALIDATION: Complex array structures (Type S/SA)
      errortype.forEach((cols) => {
        entries.forEach(([key, val]) => {
          let value = "";
          let value1 = "";
          let msg = "This field is required";

          if (cols.Type === "S") {
            value = detailObj[key][cols.Column][0].Actual_Value[0];
            value1 = detailObj[key][cols.Column][1].Range_Qualified[0];
          }
          if (cols.Type === "SA") {
            value = detailObj[key][cols.Column][0].Actual_Value[0];
            value1 = detailObj[key][cols.Column][1].Range_Qualified[0].Range_Qualified1[0];
          }

          if (
            value === undefined ||
            value === "" ||
            value === null ||
            value1 === undefined ||
            value1 === "" ||
            value1 === null
          ) {
            isError = true;
            setProcessErrors(prev => prev.map((val, i) => i === +key ? true : val));
            const fieldError = [
              { Actual_Value: [{ status: true, message: msg }] },
              { Range_Qualified: [{ status: true, message: msg }] },
            ];
            setErrorAt(+key)(prev => ({ ...prev, [cols.Column]: fieldError }));
          }
        });
      });

      return { isError };
    };

    const handleAdd = async (savetype) => {
      let result = false;
      if (
        headerObj?.WPSH_Status === Status.StatusApproved ||
        headerObj?.WPSH_Status === Status.StatusRejected
      ) {
        headerObj.WPSH_Status = Status.StatusPending;
        if (headerObj?.WPSH_Status === Status.StatusApproved) headerObj.WPS_Change_No = handleCount(headerObj.WPS_Change_No);
      }

      if (detailObj?.wld_process_status === "Active") {
        headerObj.WPQ_Status = "Active";
      } else if (detailObj?.wld_process_status === "Inactive") {
        headerObj.WPQ_Status = "Inactive";
      }

      const filteredDetails = detailObj.filter(
        (item) => item.WPQ_Process !== "NA" && item.WPQ_Process !== ""
      );

      // PROCESSING: Updates qualification arrays for WI field extraction
      let res = await AddNewGrooveWPQ(headerObj);
      let postList = createWPQRangeQualPostlist(detailObj);

      let detaildata = detailObj?.filter((item) => item.WPQ_Process !== "");
      for (const item of detaildata) {
        await AddNewDetail(item).then(async (r) => {
          result = true;
          if (
            action === Action.ActionEdit &&
            savetype !== Status.StatusTempSaved
          ) {
            result = true;
          }
        });
      }

      if (res !== undefined && result != "SpecialCharacters") {
        result = true;
        setAction(Action.ActionEdit);
      } else {
        showWarning(process.env.REACT_APP_SPECIAL_CHARACTER_MSG);
      }
      return result;
    };

    const handleSelect = (e) => {
      Temp(e, "tab");
    };

    const Temp = async (e, tabaction) => {
      let res = false;

      res = await firstChildRef.current.funcOnNextTemp();

      if (res == MessageTypes.Temp || res == MessageTypes.None) {
        if (res == MessageTypes.Temp) {
          showSuccess(ToastMessages[res]);
        }
        setKeyprocess(+e);
      } else {
        showWarning(ToastMessages[res]);
      }

    };

    let Oneprocess =
      detailObj[0]?.WPQ_Process === "" ||
      detailObj[0]?.WPQ_Process === undefined;
    let Twoprocess =
      detailObj[1]?.WPQ_Process === "" ||
      detailObj[1]?.WPQ_Process === undefined;
    let Threeprocess =
      detailObj[2]?.WPQ_Process === "" ||
      detailObj[2]?.WPQ_Process === undefined;

    const Processstatus = async (data) => {
      let Process_Status = [];
      let detail_SK = [];

      data.forEach((item) => {
        Process_Status.push(item.wld_process_status);

        detail_SK.push(item.WPQ_Process + "#" + item.WPQ_Type);
      });
      setheaderObj({ ...headerObj, Process_Status, detail_SK });

    };

    useEffect(() => {
      let WpqDetail = detailObj?.filter(
        (i) => i.WPQ_Process !== "NA" && i.WPQ_Process !== ""
      );
      if (WpqDetail.length > 0) {
        Processstatus(WpqDetail);
      }
    }, []);



    return (
      <Row className="font">
        {loading && (
          <div className="loader-overlay">
            <DotLoader size={80} className="pageloader" />
          </div>
        )}
        <WPQHeader headerObj={headerObj}></WPQHeader>
        <div className="mt-3">
          <div
            style={{
              borderBottom: "2px solid var(--col-ddd)",
              position: "relative",
              top: "42px",
              zIndex: "-2",
            }}
          ></div>

          <Tabs
            activeKey={keyprocess}
            onSelect={(e) => handleSelect(e)}
            className="mb-3 w-100 border-end"
            fill
          >
            <Tab
              eventKey={1}
              title={
                <div>
                  {processErrors[0] ? (
                    <ToolError title="Get WPQ Detail - Process 1" />
                  ) : (
                    "Get WPQ Detail - Process 1"
                  )}
                </div>
              }
              style={{
                height: window.innerHeight - 180,
                overflowY: "auto",
                overflowX: "hidden",
              }}
              disabled={!!Oneprocess}
            >
              <WPQGetDetailProcess
                wpq_process={detailObj[0]}
                Index={0}
                ref={firstChildRef}
                headerObj={headerObj}
                setheaderObj={setheaderObj}
                detailObj={detailObj}
                setdetailObj={setdetailObj}
                error={error[0]}
                setError={setErrorAt(0)}
                isDisabled={isDisabled}
                isReadonly={isReadonly}
                IsEdited={IsEdited}
                setIsEdited={setIsEdited}
              />
            </Tab>
            <Tab eventKey={2} title={
              <div>
                {processErrors[1] ? (
                  <ToolError title="Get WPQ Detail - Process 2" />
                ) : (
                  "Get WPQ Detail - Process 2"
                )}
              </div>
            }
              style={{
                height: window.innerHeight - 180,
                overflowY: "auto",
                overflowX: "hidden",
              }}
              disabled={!!Twoprocess}
            >
              <WPQGetDetailProcess
                wpq_process={detailObj[1]}
                Index={1}
                headerObj={headerObj}
                setheaderObj={setheaderObj}
                detailObj={detailObj}
                setdetailObj={setdetailObj}
                error={error[1]}
                setError={setErrorAt(1)}
                isDisabled={isDisabled}
                isReadonly={isReadonly}
                IsEdited={IsEdited}
                setIsEdited={setIsEdited}
              />
            </Tab>
            <Tab
              eventKey={3}
              title={
                <div>
                  {processErrors[2] ? (
                    <ToolError title="Get WPQ Detail - Process 3" />
                  ) : (
                    "Get WPQ Detail - Process 3"
                  )}
                </div>
              }
              style={{
                height: window.innerHeight - 180,
                overflowY: "auto",
                overflowX: "hidden",
              }}
              disabled={!!Threeprocess}
            >
              <WPQGetDetailProcess
                wpq_process={detailObj[2]}
                Index={2}
                headerObj={headerObj}
                setheaderObj={setheaderObj}
                detailObj={detailObj}
                setdetailObj={setdetailObj}
                error={error[2]}
                setError={setErrorAt(2)}
                isDisabled={isDisabled}
                isReadonly={isReadonly}
                IsEdited={IsEdited}
                setIsEdited={setIsEdited}
              />
            </Tab>
          </Tabs>
        </div>
      </Row>
    );
  }
);

export default WPQ_GetDetails;